import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useActiveFamily } from '../../context/ActiveFamilyContext';
import { setFamilyPIN, verifyFamilyPIN } from '../../services/familyPINService';

interface ParentalPINModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title: string;
  description?: string;
  mode: 'setup' | 'verify';
  children?: React.ReactNode;
}

const MAX_ATTEMPTS = 3;
const LOCKOUT_TIME = 5 * 60 * 1000; // 5 minutos
const PIN_LENGTH = 4;

export default function ParentalPINModal({
  isOpen,
  onClose,
  onSuccess,
  title,
  description,
  mode,
  children
}: ParentalPINModalProps) {
  const { t } = useTranslation();
  const { activeFamily } = useActiveFamily();
  const [pin, setPin] = useState<string[]>(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState<string[]>(['', '', '', '']);
  const [step, setStep] = useState<'enter' | 'confirm'>('enter');
  const [error, setError] = useState<string>('');
  const [attempts, setAttempts] = useState<number>(0);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [lockoutEnd, setLockoutEnd] = useState<number>(0);
  const [savingPIN, setSavingPIN] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Verificar si hay lockout activo
  useEffect(() => {
    const storedLockout = localStorage.getItem('pin_lockout_end');
    if (storedLockout) {
      const lockoutEndTime = parseInt(storedLockout);
      if (Date.now() < lockoutEndTime) {
        setIsLocked(true);
        setLockoutEnd(lockoutEndTime);
        
        const timer = setInterval(() => {
          if (Date.now() >= lockoutEndTime) {
            setIsLocked(false);
            setAttempts(0);
            localStorage.removeItem('pin_lockout_end');
            clearInterval(timer);
          }
        }, 1000);
        
        return () => clearInterval(timer);
      } else {
        localStorage.removeItem('pin_lockout_end');
      }
    }
  }, []);

  // Reset al abrir/cerrar
  useEffect(() => {
    if (isOpen) {
      setPin(['', '', '', '']);
      setConfirmPin(['', '', '', '']);
      setStep('enter');
      setError('');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [isOpen]);

  // iOS keyboard/viewport mitigation: prevent the document from scrolling/reflowing under the modal
  useEffect(() => {
    if (!isOpen) return;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyTop = body.style.top;
    const prevBodyWidth = body.style.width;

    const scrollY = window.scrollY || 0;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.position = prevBodyPosition;
      body.style.top = prevBodyTop;
      body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  const handleSubmit = async (pinToValidate?: string[]) => {
    if (!activeFamily?.id) {
      setError(t('pin.noActiveFamily'));
      return;
    }

    // Usar el PIN pasado como parámetro o el del estado
    const enteredPin = pinToValidate || (step === 'enter' ? pin : confirmPin);
    
    if (enteredPin.some(digit => digit === '')) {
      setError(t('pin.enter4Digits'));
      return;
    }

    if (mode === 'setup') {
      if (step === 'enter') {
        // Pasar a confirmación
        setStep('confirm');
        setError('');
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else {
        // Verificar que coincidan
        const enteredPinString = pin.join('');
        const confirmPinString = enteredPin.join('');
        
        if (enteredPinString === confirmPinString) {
          // Guardar PIN en Firestore
          setSavingPIN(true);
          try {
            await setFamilyPIN(activeFamily.id, enteredPinString);
            
            setTimeout(() => {
              onSuccess();
              onClose();
            }, 300);
          } catch (error) {
            setError(t('pin.savePINError'));
            console.error('Error saving PIN:', error);
          } finally {
            setSavingPIN(false);
          }
        } else {
          setError(t('pin.pinMismatch'));
          setStep('enter');
          setPin(['', '', '', '']);
          setConfirmPin(['', '', '', '']);
          setTimeout(() => {
            setError('');
            inputRefs.current[0]?.focus();
          }, 2000);
        }
      }
      return;
    }

    // Modo verify
    setSavingPIN(true);
    try {
      const enteredPinString = enteredPin.join('');
      const isValid = await verifyFamilyPIN(activeFamily.id, enteredPinString);
      
      if (isValid) {
        // PIN correcto
        setAttempts(0);
        localStorage.removeItem('pin_lockout_end');
        
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 300);
      } else {
        // PIN incorrecto
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= MAX_ATTEMPTS) {
          // Bloquear por 5 minutos
          const lockoutEndTime = Date.now() + LOCKOUT_TIME;
          setLockoutEnd(lockoutEndTime);
          setIsLocked(true);
          localStorage.setItem('pin_lockout_end', lockoutEndTime.toString());
          setError(t('pin.tooManyAttempts'));
        } else {
          const remainingAttempts = MAX_ATTEMPTS - newAttempts;
          setError(t('pin.wrongPIN', { count: remainingAttempts }));
        }
        
        // Limpiar campos
        setTimeout(() => {
          if (step === 'enter') setPin(['', '', '', '']);
          else setConfirmPin(['', '', '', '']);
          setError('');
          inputRefs.current[0]?.focus();
        }, 2000);
      }
    } catch (error) {
      setError(t('pin.verifyPINError'));
      console.error('Error verifying PIN:', error);
    } finally {
      setSavingPIN(false);
    }
  };

  const handleInputChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return; // Solo números
    
    const currentPinArray = step === 'enter' ? pin : confirmPin;
    const newPin = [...currentPinArray];
    
    if (value.length === 0) {
      // Borrar
      newPin[index] = '';
      if (step === 'enter') setPin(newPin);
      else setConfirmPin(newPin);
      
      // Focus anterior
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (value.length === 1) {
      // Agregar dígito
      newPin[index] = value;
      if (step === 'enter') setPin(newPin);
      else setConfirmPin(newPin);
      
      // Auto-focus siguiente
      if (index < PIN_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      } else {
        // PIN completo, enviar con el PIN actualizado
        setTimeout(() => handleSubmit(newPin), 100);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      const currentPinArray = step === 'enter' ? pin : confirmPin;
      if (currentPinArray[index] === '' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const getRemainingTime = () => {
    if (!isLocked) return '';
    const remaining = Math.ceil((lockoutEnd - Date.now()) / 1000);
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  const currentPinArray = step === 'enter' ? pin : confirmPin;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/50"
      style={{ paddingBottom: 'calc(var(--keyboard-height, 0px) + 1rem)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-[24px] shadow-sm max-h-[80vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: '#fff5eb', maxHeight: 'calc(var(--app-height, 100dvh) - 2rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-5 rounded-t-[24px]"
          style={{ backgroundColor: '#055590' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <div className="text-2xl">🔒</div>
            </div>
            <h2 className="text-xl font-black text-white">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-2xl hover:bg-white/20 flex items-center justify-center transition-colors"
            disabled={isLocked || savingPIN}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Status */}
          {mode === 'setup' && (
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm font-medium">
                {step === 'enter' ? (
                  <>
                    <span>{t('pin.step1Label')}</span>
                    <span>{t('pin.createPIN')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('pin.step2Label')}</span>
                    <span>{t('pin.confirmPIN')}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Lockout Message */}
          {isLocked ? (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">⏱️</div>
              <p className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
                {t('pin.temporarilyLocked')}
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('pin.tooManyFailedAttempts')}
              </p>
              <div className="text-3xl font-mono font-bold text-gray-900 dark:text-white">
                {getRemainingTime()}
              </div>
            </div>
          ) : (
            <>
              {/* PIN Inputs */}
              <div className="flex justify-center gap-3 mb-6">
                {currentPinArray.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    type="password"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-16 h-16 text-center text-2xl font-bold border-2 border-gray-300 dark:border-gray-700 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 outline-none transition-all bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
                    disabled={isLocked || savingPIN}
                  />
                ))}
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-700 dark:text-red-300 text-center">
                    {error}
                  </p>
                </div>
              )}

              {/* Info */}
              <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                {mode === 'setup' ? (
                  step === 'enter' ? (
                    <p>{t('pin.enterPINHint')}</p>
                  ) : (
                    <p>{t('pin.confirmPINHint')}</p>
                  )
                ) : (
                  <p>{t('pin.enterParentalPIN')}</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 space-y-2">
          <button
            onClick={onClose}
            className="w-full py-3 px-4 rounded-xl border-2 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            disabled={isLocked || savingPIN}
          >
            {t('common.cancel')}
          </button>
          
          {/* Children: botones adicionales como "Otros métodos" */}
          {children}
        </div>
      </div>
    </div>
  );
}
